import { styled } from "@linaria/react"
import { ToggleGroupItem } from "@radix-ui/react-toggle-group"
import { useEffect, useRef, useState } from "react"

import { ToggleGroup } from "@/components/ToggleGroup/ToggleGroup.tsx"
import { getLogger } from "@/lib/logging.ts"
import { focusOutline } from "@/styles/mixins.ts"
import { baseFontSize } from "@/styles/textStyles.ts"

import type { LinariaClassName } from "@linaria/core"

const log = getLogger("PillToggleGroup")

export interface PillToggleGroupProps<T> {
  items: T[]
  selectedItem: T
  onItemChange: (value: T) => void
  getKey: (item: T) => string
  getValue: (item: T) => string
  className?: LinariaClassName
}

// eslint-disable-next-line react/function-component-definition
export function PillToggleGroup<T>(
  {
    selectedItem,
    onItemChange,
    items,
    getKey,
    getValue,
    className
  }: PillToggleGroupProps<T>
) {
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 })
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  const [hasInteracted, setHasInteracted] = useState(false)

  useEffect(() => {
    const activeIndex = items.findIndex((item) => getKey(item) === getKey(selectedItem))
    const activeElement = itemRefs.current[activeIndex]

    if (activeElement && activeElement.parentElement) {
      let width = activeElement.offsetWidth
      let left = activeElement.offsetLeft

      const edgePadding = 8
      const firstItemExtraWidth = 12
      const lastItemExtraWidth = 14

      const isFirst = activeIndex === 0
      if (isFirst) {
        width += firstItemExtraWidth
        left -= edgePadding
      }

      const isLast = activeIndex === items.length - 1
      if (isLast) {
        width += lastItemExtraWidth
        left -= edgePadding / 2
      }

      setIndicatorStyle({
        width: width,
        left: left,
      })
    }
  }, [selectedItem, items, getKey])

  if (items.length <= 1) {
    return null
  }

  return (
    <PillToggleGroupContainer
      type="single"
      value={getValue(selectedItem)}
      className={className}
      onValueChange={(val) => {
        if (val) {
          const foundItem = items.find((item) => getValue(item) === val)
          if (foundItem) {
            setHasInteracted(true)
            onItemChange(foundItem)
          } else {
            log.warn("item is not found on selection")
          }
        }
      }}
    >
      <PillToggleGroupIndicator
        width={indicatorStyle.width}
        left={indicatorStyle.left}
        animated={hasInteracted}
      />

      {items.map((item, index) => (
        <PillToggleGroupItem
          key={getKey(item)}
          value={getValue(item)}
          ref={(el) => {
            itemRefs.current[index] = el
          }}
        >
          {getValue(item)}
        </PillToggleGroupItem>
      ))}
    </PillToggleGroupContainer>
  )
}

const PillToggleGroupContainer = styled(ToggleGroup)`
  position: relative;
  display: inline-flex;
  align-items: center;

  height: 32px;
  padding: 0 8px;

  background-color: var(--background-color-10);

  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-40);
`

const PillToggleGroupIndicator = styled.div<{ width: number, left: number, animated: boolean }>`
  position: absolute;
  left: -1px;
  top: -1px;

  width: ${props => props.width}px;
  height: 32px;

  border: 1px solid rgb(157 157 157 / 100%);
  border-radius: var(--border-radius-40);

  box-shadow: 0 2px 6px 0 rgb(2 2 20 / 10%);

  transform: translateX(${props => props.left}px);
  transition: ${props => props.animated ? "transform 300ms ease-out, width 300ms ease-out" : "none"};
  will-change: ${props => props.animated ? "transform, width" : "auto"};
`

const PillToggleGroupItem = styled(ToggleGroupItem)`
  ${baseFontSize};
  line-height: 120%;
  font-weight: 400;

  padding: 8px;
  border-radius: var(--border-radius-40);
  outline: none;

  &:focus-visible {
    ${focusOutline};
  }

  &:first-of-type {
    padding-left: 4px;
  }

  &:last-child {
    padding-right: 4px;
  }

  &[data-state="off"] {
    color: var(--secondary-text-color);
  }
`
